import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';
import { orderBy } from 'natural-orderby';
import { InfoAlert } from 'smartsuite-ui';
import WidgetErrorMessage from '../../WidgetErrorMessage';

import PositiveNegativeBarGraph from './PositiveNegativeBarGraph';
import { formatTimeSeriesDataset } from '../../utils/timeSeriesDataFormatUtils';
import {
    getMetricSourceForRequest,
    hasMetricSources,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import {
    getErrorMessageKey,
    getTimeSeriesTickFormatter,
    getFormattedTimeFromMilliseconds,
} from '../../utils/dataFormatUtils';

function getThresholdErrorKey(thresholds) {
    return isEmpty(thresholds) || thresholds.some((x) => x.value === undefined)
        ? 'invalidThresholdConfig'
        : null;
}

/* eslint-disable complexity */
function getMetricSource({ metricRequests, metrics }) {
    if (metricRequests.length === 0 || metricRequests[0].metrics.length === 0) {
        return { errorKey: 'invalidMetricRequests' };
    }

    const metricSource = getMetricSourceForRequest(metrics, metricRequests[0]);
    if (!metricSource) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized([metricSource])) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources([metricSource])) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [metricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSource, metricRequest: metricRequests[0] };
}

function formatGraphData(point, thresholdValue, metric) {
    // prettier-ignore
    return point[metric] < thresholdValue
        ? {
            ...point,
            [metric]: thresholdValue - point[metric],
            hidden: point[metric],
        }
        : {
            ...point,
            [metric]: point[metric] - thresholdValue,
            hidden: thresholdValue,
        };
}

function getChartDomain(data, metric, thresholdValue) {
    const dataMaxDiff = Math.max(
        ...data.map((point) =>
            point.hidden === thresholdValue
                ? point[metric]
                : thresholdValue - point.hidden
        )
    );

    return [thresholdValue - dataMaxDiff, thresholdValue + dataMaxDiff];
}

function processSingleMetric(
    metricRequest,
    metricSource,
    dataLabel,
    numberDecimalFormatter,
    disconnectedGraph
) {
    const metricValueKey = metricRequest.metrics[0];
    const metricNameKey = metricRequest.groupBy[0];
    const formattedData = formatTimeSeriesDataset(
        metricSource.bucketTimes,
        metricSource.metricSeries,
        [dataLabel],
        metricNameKey,
        metricValueKey,
        numberDecimalFormatter,
        disconnectedGraph
    );

    return formattedData;
}

export default function PositiveNegativeBarGraphWrapper(props) {
    const { metricSource, metricRequest, metricSourceErrors, errorKey } =
        getMetricSource(props);
    const wrapperRef = useRef();
    const [activeIndex, setActiveIndex] = useState();
    const [titleWidth, setTitleWidth] = useState(0);

    useEffect(() => {
        if (wrapperRef?.current) {
            const elements = Array.from(
                wrapperRef.current.querySelectorAll(
                    '.selector-container__title'
                )
            );
            const maxWidth = Math.max(...elements.map((el) => el.clientWidth));
            setTitleWidth(maxWidth);
        }
    }, []);

    const errorMessageKey = getErrorMessageKey(
        errorKey,
        getThresholdErrorKey(props.thresholds)
    );
    if (errorMessageKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorMessageKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const dataFormatter = props.configuration.isTime
        ? getFormattedTimeFromMilliseconds
        : undefined;

    const metricSourceGroupedSeries = groupBy(
        metricSource.metricSeries,
        metricRequest.groupBy[0]
    );

    const dataLabels = orderBy(Object.keys(metricSourceGroupedSeries));
    const defaultThreshold = props.thresholds.find((x) => !x.itemId);

    return (
        <>
            {metricSource.isTruncated && (
                <InfoAlert>
                    {`${props.dashboardLabels.truncationWarning} ${metricRequest.truncationThreshold}`}
                </InfoAlert>
            )}
            <div className="chart-container" ref={wrapperRef}>
                {dataLabels.map((dataLabel, index) => {
                    const metricSeries = metricSourceGroupedSeries[dataLabel];
                    const rawDataForSelector = metricSeries[0];

                    const data = processSingleMetric(
                        metricRequest,
                        { ...metricSource, metricSeries },
                        dataLabel,
                        props.numberDecimalFormatter,
                        props.configuration.disconnectedGraph
                    );

                    const threshold =
                        props.thresholds.find((x) => x.itemId === dataLabel) ??
                        defaultThreshold;

                    const formattedData = data.map((point) =>
                        formatGraphData(point, threshold.value, dataLabel)
                    );

                    const domain = getChartDomain(
                        formattedData,
                        dataLabel,
                        threshold.value
                    );

                    const displayLabel =
                        metricSeries[0][props.configuration.displayGroupKey] ??
                        dataLabel;

                    return (
                        <PositiveNegativeBarGraph
                            key={dataLabel}
                            chartProperties={props.configuration}
                            dataFormatter={dataFormatter}
                            dataLabel={dataLabel}
                            dataPoints={formattedData}
                            dateFormatter={getTimeSeriesTickFormatter(
                                metricRequest.timeAndPrecision.precisionType
                            )}
                            domain={domain}
                            isActive={activeIndex === index}
                            isSelectable={props.isSelectable}
                            metricValueKey={metricRequest.metrics[0]}
                            onSelectorClick={() => {
                                props.onSelectorClick(rawDataForSelector);
                                setActiveIndex(index);
                            }}
                            onSelectorRemoveClick={() => {
                                props.onSelectorRemoveClick();
                                setActiveIndex(undefined);
                            }}
                            threshold={threshold}
                            title={displayLabel}
                            titleWidth={titleWidth}
                        />
                    );
                })}
            </div>
        </>
    );
}
/* eslint-enable complexity */

PositiveNegativeBarGraphWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    isSelectable: PropTypes.bool.isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
    onSelectorClick: PropTypes.func.isRequired,
    onSelectorRemoveClick: PropTypes.func.isRequired,
    thresholds: PropTypes.instanceOf(Array).isRequired,
};
